// Away hunting (Cole, 2026-10-20): after the game closes, the team keeps hunting at a quarter of the pace it
// actually had, coins and XP only, credited at the next log-in (server/world.ts). Never catches, Auras, drops,
// shards, boss fights, quests, pass progress or eggs. The numbers are in CONFIG.offline.
import { CONFIG } from './config.ts';
import { utcDay } from './quests.ts';

/** What a session's hunt earned over its last CONFIG.offline.windowMs of hunting; saved with its session row. */
export interface AwayPace {
  /** The hunt it was on at the end. */
  ground: string;
  /** Hunting time the sums cover, rests after a knockout included. */
  ms: number;
  /** Hunting, rests left out, carried on from the session before (server/world.ts): away hunting needs CONFIG.offline.minHuntMs of it. */
  huntMs: number;
  coins: number;
  xp: number;
  /** Creature XP by creature id. */
  cx: [number, number][];
}

/** Away time counted on one UTC day (TrainerDTO.awayHunt), and the last session row credited. */
export interface AwayBudget { day: string; usedMs: number; session: number }

/** About a minute of hunting: its time and what it earned. */
interface Slice { ms: number; coins: number; xp: number; cx: Map<number, number> }
const SLICE_MS = 60_000;

/** One session's hunt as the server saw it: kill coins, trainer XP and creature XP against hunting time. */
export class HuntMeter {
  /** Hunting this session, rests after a knockout left out. */
  huntMs = 0;
  /** Time in the slices kept. */
  private ms = 0;
  private slices: Slice[] = [];

  private add(): Slice {
    const s: Slice = { ms: 0, coins: 0, xp: 0, cx: new Map() };
    this.slices.push(s);
    return s;
  }

  /** The hunt ran for `ms`. Resting after a knockout counts toward the pace but not toward the session's hunting. */
  time(ms: number, resting = false) {
    if (!(ms > 0)) return;
    // A kill goes with the time before it, so a new slice starts only here.
    const last = this.slices.at(-1);
    (last && last.ms < SLICE_MS ? last : this.add()).ms += ms;
    this.ms += ms;
    if (!resting) this.huntMs += ms;
    // Only the newest window is kept.
    while (this.slices.length > 1 && this.ms - this.slices[0].ms >= CONFIG.offline.windowMs) this.ms -= this.slices.shift()!.ms;
  }

  /** One kill: its coins, trainer XP and each creature's XP ([id, xp]). */
  earn(coins: number, xp: number, cx: Iterable<readonly [number, number]>) {
    const s = this.slices.at(-1) ?? this.add();
    s.coins += coins;
    s.xp += xp;
    for (const [id, n] of cx) if (n > 0) s.cx.set(id, (s.cx.get(id) ?? 0) + n);
  }

  /** The pace so far, also under the minimum (a session cut by a restart carries on: from()); undefined before any hunting. */
  pace(ground: string): AwayPace | undefined {
    if (this.ms <= 0) return undefined;
    let coins = 0, xp = 0;
    const cx = new Map<number, number>();
    for (const s of this.slices) {
      coins += s.coins; xp += s.xp;
      for (const [id, n] of s.cx) cx.set(id, (cx.get(id) ?? 0) + n);
    }
    return { ground, ms: this.ms, huntMs: this.huntMs, coins, xp, cx: [...cx] };
  }

  /** A meter that carries on from a pace: a session the server ended a moment ago (a restart) goes on counting. */
  static from(p: AwayPace): HuntMeter {
    const m = new HuntMeter();
    const n = Math.max(1, Math.ceil(p.ms / SLICE_MS));
    for (let i = 0; i < n; i++) { m.time(p.ms / n); m.earn(p.coins / n, p.xp / n, p.cx.map(([id, x]) => [id, x / n] as const)); }
    m.huntMs = p.huntMs;
    return m;
  }
}

/** A pace read back from a session row; undefined when it is missing or malformed. */
export function parsePace(json: string | null | undefined): AwayPace | undefined {
  if (!json) return undefined;
  try {
    const p = JSON.parse(json) as AwayPace;
    return p && typeof p.ground === 'string' && p.ms > 0 && p.huntMs >= 0 && Array.isArray(p.cx) ? p : undefined;
  } catch { return undefined; }
}

export interface AwayCredit {
  /** Away time counted, and whether today's limit cut it short. */
  ms: number; capped: boolean;
  coins: number; xp: number; cx: [number, number][];
  /** Today's count after this. */
  budget: { day: string; usedMs: number };
}

/**
 * What `awayMs` away earns at CONFIG.offline.pace of `p`, within the day's budget (the UTC day of `now`): undefined
 * when nothing counts (too little hunting, a short absence, today's time used up, or a pace that earned nothing).
 */
export function awayCredit(p: AwayPace, awayMs: number, budget: { day: string; usedMs: number } | undefined, now: number): AwayCredit | undefined {
  const o = CONFIG.offline, day = utcDay(now);
  if (!(p.huntMs >= o.minHuntMs) || !(awayMs >= o.minAwayMs) || !(p.ms > 0)) return undefined;
  const used = budget?.day === day ? budget.usedMs : 0;
  const ms = Math.min(awayMs, o.maxAwayPerDayMs - used);
  if (ms <= 0) return undefined;
  const k = (o.pace * ms) / p.ms;
  const coins = Math.floor((p.coins || 0) * k), xp = Math.floor((p.xp || 0) * k);
  const cx = (p.cx ?? []).map(([id, n]) => [id, Math.floor((n || 0) * k)] as [number, number]).filter(([, n]) => n > 0);
  if (!coins && !xp && !cx.length) return undefined;
  return { ms, capped: ms < awayMs, coins, xp, cx, budget: { day, usedMs: used + ms } };
}

/** How the pace reads to players: "quarter pace" at the shipped setting. */
export function awayPaceName(): string {
  const p = CONFIG.offline.pace;
  return p === 0.25 ? 'quarter pace' : p === 0.5 ? 'half pace' : `${Math.round(p * 100)}% pace`;
}
